/**
 * 15分刻み歩数バー（1時間分）
 *
 * 指定された時間の4つの15分枠を横並びで表示
 */

import { Footprints } from 'lucide-react'

import type { GarminSteps15Min } from '@/api/types/garmin'
import { cn } from '@/lib/utils'

type StepsHourBarProps = {
  hour: number
  steps15Min: GarminSteps15Min[]
  maxSteps: number // 全データ中の最大歩数（統一スケール用）
}

// アクティビティレベルに応じた色
const getBarColor = (level: string, steps: number) => {
  if (steps === 0) return 'bg-muted/30'
  switch (level) {
    case 'highlyActive':
      return 'bg-green-500'
    case 'active':
      return 'bg-blue-500'
    case 'sedentary':
      return 'bg-amber-500'
    case 'sleeping':
      return 'bg-purple-400'
    default:
      return 'bg-gray-400'
  }
}

/**
 * 1時間分の歩数バー（4つの15分枠）
 */
export const StepsHourBar = ({ hour, steps15Min, maxSteps }: StepsHourBarProps) => {
  // この時間帯の15分データを取得
  const hourStr = String(hour).padStart(2, '0')
  const slots = steps15Min.filter((s) => s.startJST.startsWith(`${hourStr}:`))

  // この時間の合計歩数
  const hourSteps = slots.reduce((sum, s) => sum + s.steps, 0)

  return (
    <div className="flex items-center gap-1 h-6" title={`${hour}時台: ${hourSteps}歩`}>
      {/* 4つの15分枠バー */}
      <div className="flex gap-0.5 flex-1">
        {[0, 15, 30, 45].map((min) => {
          const minStr = String(min).padStart(2, '0')
          const slot = slots.find((s) => s.startJST === `${hourStr}:${minStr}`)
          const steps = slot?.steps ?? 0
          const level = slot?.activityLevel ?? 'unknown'
          const barHeight = steps > 0 ? Math.max((steps / maxSteps) * 100, 10) : 0

          return (
            <div key={min} className="flex-1 h-full flex items-end" title={`${hourStr}:${minStr} - ${steps}歩`}>
              <div
                className={cn('w-full rounded-t-sm transition-all', getBarColor(level, steps))}
                style={{ height: steps > 0 ? `${barHeight}%` : '2px' }}
              />
            </div>
          )
        })}
      </div>

      {/* 歩数表示 */}
      <span
        className={cn(
          'text-xs w-12 text-right tabular-nums',
          hourSteps === 0 ? 'text-muted-foreground/50' : 'text-muted-foreground',
          hourSteps >= 500 && 'font-medium text-foreground',
        )}
      >
        {hourSteps > 0 ? hourSteps.toLocaleString() : '-'}
      </span>
    </div>
  )
}

type StepsSummaryProps = {
  steps15Min: GarminSteps15Min[]
}

/**
 * 歩数サマリー（1日の合計）
 */
export const StepsSummary = ({ steps15Min }: StepsSummaryProps) => {
  const totalSteps = steps15Min.reduce((sum, s) => sum + s.steps, 0)

  return (
    <div className="flex items-center gap-1.5 text-sm">
      <Footprints className="size-4 text-muted-foreground" />
      <span className="font-medium">{totalSteps.toLocaleString()}歩</span>
    </div>
  )
}
